import type { IAhuBoardSection, IAhuBoardTunnel, ICmsAhuDoc } from 'models';
import { cmsTunnelIndexes, cmsTunnelConfigurations, type CmsNamedRow } from '../forms/cmsDoc';

/**
 * Phase 6e1 — `fn.tools.get_possible_bindings_for_insertion` port (§9.3 /
 * view/fn_tools.js:155-340, algorithm verbatim): computes the insert-popup
 * matrix — used indices, current configuration, occupied index blocks for
 * 1×1 / 2×1 components, resulting configuration per candidate, `active:false`
 * when the target tunnel pair already holds a sitting_on_id section.
 */

export interface InsertionBinding {
  base_tunnel_index: CmsNamedRow;
  occupied_tunnel_indexes: CmsNamedRow[];
  occupied_tunnel_sections: Array<string[] | undefined>;
  occupied_tunnel_sections_last_index: number[];
  resulting_configuration: CmsNamedRow | undefined;
  active: boolean;
  has_sections: boolean;
}

const valueGetter = (confOrIndex: CmsNamedRow | string): number[] =>
  (typeof confOrIndex === 'string' ? confOrIndex : String(confOrIndex.value ?? ''))
    .split('_')
    .map((el) => parseInt(el, 10));

export function getPossibleBindingsForInsertion(args: {
  isDualTunnel: boolean;
  tunnels: IAhuBoardTunnel[];
  sectionsByTunnel: Record<number, IAhuBoardSection[]>;
  cms: ICmsAhuDoc | undefined;
}): InsertionBinding[] {
  const { isDualTunnel, tunnels, sectionsByTunnel, cms } = args;
  const tunnelIndexes = cmsTunnelIndexes(cms);
  const tunnelConfigurations = cmsTunnelConfigurations(cms);
  if (!tunnelIndexes.length || !tunnels.length) {
    // Empty AHU still gets every base index as a candidate.
  }

  const usedIndices = tunnels
    .map((tunnel) => tunnelIndexes.find((el) => el.id === tunnel.index_id))
    .filter((i): i is CmsNamedRow => !!i)
    .sort((i1, i2) => {
      const a = valueGetter(i1);
      const b = valueGetter(i2);
      return a[0] - b[0] + a[1] - b[1];
    });

  const lastUsed = usedIndices[usedIndices.length - 1] ?? tunnelIndexes[0];
  const currentConfiguration =
    tunnelConfigurations.find((conf) => conf.value === lastUsed?.value) ?? lastUsed;
  const currentValues = currentConfiguration ? valueGetter(currentConfiguration) : [1, 1];

  const componentSize: [number, number] = [isDualTunnel ? 2 : 1, 1];

  const getResultingConfiguration = (indexes: CmsNamedRow[]): CmsNamedRow | undefined => {
    let maxX = currentValues[0];
    let maxY = currentValues[1];
    for (const index of indexes) {
      const values = valueGetter(index);
      if (values[0] > maxX) maxX = values[0];
      if (values[1] > maxY) maxY = values[1];
    }
    if (maxX <= currentValues[0] && maxY <= currentValues[1]) return currentConfiguration;
    const value = `${maxX}_${maxY}`;
    return tunnelConfigurations.find((conf) => conf.value === value);
  };

  const getIndexFromValue = (value: string) =>
    tunnelIndexes.find((ti) => ti.value === value);

  const getOccupiedBlocks = (baseValue: string): string[][] => {
    const res: string[][] = [[], []];
    const base = valueGetter(baseValue);
    for (let y = base[1]; y < base[1] + componentSize[1]; y += 1) {
      for (let x = base[0]; x < base[0] + componentSize[0]; x += 1) {
        res[0].push(`${x}_${y}`);
      }
    }
    if (componentSize[0] === componentSize[1] && componentSize[0] === 1) {
      return [res[0]];
    }
    for (let y = base[1]; y < base[1] + componentSize[0]; y += 1) {
      for (let x = base[0]; x < base[0] + componentSize[1]; x += 1) {
        res[1].push(`${x}_${y}`);
      }
    }
    return res;
  };

  const firstTunnelId = Object.keys(sectionsByTunnel)[0];
  const firstHasSections = firstTunnelId
    ? (sectionsByTunnel[Number(firstTunnelId)] ?? []).length > 0
    : false;

  const checkConfiguration = (configuration: CmsNamedRow): boolean => {
    const values = valueGetter(configuration);
    const d = firstHasSections ? 0 : 1;
    return (
      (values[0] <= currentValues[0] + componentSize[0] - d &&
        values[1] <= currentValues[1] + componentSize[1] - d) ||
      (values[0] <= currentValues[0] + componentSize[1] - d &&
        values[1] <= currentValues[1] + componentSize[0] - d)
    );
  };

  const result: InsertionBinding[] = [];

  for (const tunnelIndex of tunnelIndexes) {
    const blocks = getOccupiedBlocks(String(tunnelIndex.value ?? ''));
    for (const block of blocks) {
      let active = true;
      let currentIndexes: CmsNamedRow[] | null = [];
      for (const value of block) {
        const ind = getIndexFromValue(value);
        if (!ind) {
          currentIndexes = null;
          break;
        }
        currentIndexes.push(ind);
      }
      if (!currentIndexes) continue;
      const resulting = getResultingConfiguration(currentIndexes);
      if (!resulting || !checkConfiguration(resulting)) continue;

      const sectionNames = currentIndexes.map((index) => {
        const tunnel = tunnels.find((t) => t.index_id === index.id);
        if (!tunnel) return undefined;
        return (sectionsByTunnel[tunnel.id] ?? []).map(
          (section) =>
            `${String(section.name ?? '')}${section.tag ? ` : ${section.tag}` : ''}`
        );
      });

      if (componentSize[0] !== 1 || componentSize[1] !== 1) {
        const tunnel = tunnels.find(
          (t) => t.index_id === currentIndexes![currentIndexes!.length - 1].id
        );
        if (tunnel) {
          active = !(sectionsByTunnel[tunnel.id] ?? []).some((s) => s.sitting_on_id);
        }
      }

      result.push({
        base_tunnel_index: tunnelIndex,
        occupied_tunnel_indexes: currentIndexes,
        occupied_tunnel_sections: sectionNames,
        occupied_tunnel_sections_last_index: sectionNames.map((sn) =>
          sn ? sn.length - 1 : -1
        ),
        resulting_configuration: resulting,
        active,
        has_sections: sectionNames.filter((el) => el).length > 0,
      });
    }
  }
  return result;
}
